import classNames from "classnames/bind";
import Button from "common/blocks/Button";
import EstimatedMarketPriceSidePanel, {
    SavingsPriceSidePanelProps,
} from "common/blocks/Sidebar/SavingsPriceSidePanel";
import UIIcon from "common/blocks/UIIcon";
import useModalContext from "common/providers/modal/useModalContext";
import { scrollIntoView } from "seamless-scroll-polyfill";
import formatCurrency from "utils/formatCurrency";

import styles from "./IncludedExtrasSummary.module.scss";

const cx = classNames.bind(styles);

export type IncludedExtrasSummaryProps = {
    extrasText: string;
    buttonText?: string;
    costOfExtras?: number;
    fractionDigits?: number;
    savingsAgainstOriginalRetailPrice: number;
    estimatedMarketPriceSavingText: string;
    listPriceSavingText: string;
    showListPriceSaving: boolean;
    savingsAgainstEstimatedMarketPrice: number;
    showEstimatedMarketPriceSaving: boolean;
    sidePanelTitle: string;
    sidePanelContent: SavingsPriceSidePanelProps;
    reserveUrl?: string;
    isComingSoon?: boolean;
};

const IncludedExtrasSummary = ({
    buttonText,
    extrasText,
    costOfExtras = 0,
    fractionDigits = 0,
    savingsAgainstOriginalRetailPrice,
    listPriceSavingText,
    showListPriceSaving,
    showEstimatedMarketPriceSaving,
    savingsAgainstEstimatedMarketPrice,
    estimatedMarketPriceSavingText,
    sidePanelTitle,
    sidePanelContent,
    reserveUrl,
    isComingSoon,
}: IncludedExtrasSummaryProps) => {
    const savingsText = showEstimatedMarketPriceSaving
        ? estimatedMarketPriceSavingText
        : listPriceSavingText;
    const savings = showEstimatedMarketPriceSaving
        ? savingsAgainstEstimatedMarketPrice
        : savingsAgainstOriginalRetailPrice;
    const savingsId = showEstimatedMarketPriceSaving
        ? "PDP-savingswrapper-marketprice"
        : "PDP-savingswrapper-listprice";

    const { useSidebar } = useModalContext();
    const { openSidebar } = useSidebar();

    const openSavingsSidebar = () => {
        openSidebar({
            title: sidePanelTitle,
            content: {
                component: () => (
                    <EstimatedMarketPriceSidePanel
                        {...sidePanelContent}
                        reserveLink={reserveUrl}
                        isComingSoon={isComingSoon}
                    />
                ),
            },
        });
    };

    const handleIncludedExtrasButtonClick = () => {
        const features = document.getElementById("included-extras");

        if (features) {
            scrollIntoView(features, { block: "center", behavior: "smooth" });
        }
    };

    return !!costOfExtras && !showListPriceSaving && !showEstimatedMarketPriceSaving ? (
        <div id="included-extras-summary" className={cx("includedExtras")}>
            <figure className={cx("costOfExtrasIcon")}>
                <UIIcon icon="SmallStar" />
            </figure>
            <div className={cx("costOfExtrasButton")}>
                <span className={cx("costOfExtras")}>
                    <strong
                        className={cx("styledPrice")}
                        dangerouslySetInnerHTML={{
                            __html: `${formatCurrency(costOfExtras, fractionDigits)}`,
                        }}
                    />
                </span>
                {!!buttonText && (
                    <Button
                        onClick={handleIncludedExtrasButtonClick}
                        type="button"
                        theme="textLink"
                        className={cx("reviewFeaturesButton")}
                        ariaLabel={buttonText}
                        id="PDP-savingswrapper-extras"
                    >
                        {extrasText}
                    </Button>
                )}
            </div>
        </div>
    ) : !!costOfExtras && (showListPriceSaving || showEstimatedMarketPriceSaving) ? (
        <div id="included-extras-summary" className={cx("includedExtrasAndListPriceSaving")}>
            <div className={cx("extrasWrapper")}>
                <figure className={cx("costOfExtrasIcon")}>
                    <UIIcon icon="SmallStar" />
                </figure>
                <div className={cx("costOfExtrasButton")}>
                    <span className={cx("costOfExtras")}>
                        <strong
                            className={cx("styledPrice")}
                            dangerouslySetInnerHTML={{
                                __html: `${formatCurrency(costOfExtras, fractionDigits)}`,
                            }}
                        />
                        <Button
                            onClick={handleIncludedExtrasButtonClick}
                            type="button"
                            theme="textLink"
                            className={cx("reviewFeaturesButton")}
                            ariaLabel={buttonText}
                            id="PDP-savingswrapper-extras"
                        >
                            {extrasText}
                        </Button>
                    </span>
                </div>
            </div>
            <div className={cx("savingsWrapper")}>
                <figure className={cx("costOfExtrasIcon", "savingsIcon")}>
                    <UIIcon icon="BlankTag" />
                </figure>
                <span className={cx("listPriceSaving")}>
                    <strong
                        className={cx("styledPrice")}
                        dangerouslySetInnerHTML={{
                            __html: `${
                                showEstimatedMarketPriceSaving ? "" : "Save"
                            } ${formatCurrency(savings, fractionDigits)}`,
                        }}
                    />
                    <Button
                        className={cx("reviewFeaturesButton")}
                        theme="textLink"
                        onClick={openSavingsSidebar}
                        ariaLabel={savingsText}
                        type="button"
                        id={savingsId}
                    >
                        <span>{savingsText}</span>
                    </Button>
                </span>
            </div>
        </div>
    ) : showListPriceSaving || showEstimatedMarketPriceSaving ? (
        <div id="included-extras-summary" className={cx("listPriceSavingNoExtras")}>
            <figure className={cx("costOfExtrasIcon")}>
                <UIIcon icon="BlankTag" />
            </figure>
            <span className={cx("listPriceSaving")}>
                <strong
                    className={cx("styledPrice")}
                    dangerouslySetInnerHTML={{
                        __html: `${showEstimatedMarketPriceSaving ? "" : "Save"} ${formatCurrency(
                            savings,
                            fractionDigits
                        )}`,
                    }}
                />
                <Button
                    className={cx("reviewFeaturesButton")}
                    theme="textLink"
                    onClick={openSavingsSidebar}
                    ariaLabel={savingsText}
                    type="button"
                    id={savingsId}
                >
                    <span>{savingsText}</span>
                </Button>
            </span>
        </div>
    ) : null;
};

export default IncludedExtrasSummary;
